/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../../../platform/Platform';
import { page as SamplePage } from '../../../../help/sample_pages/GridPage';
import {
  ComponentManagerFn,
  RequiredItem,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import {
  baseColumnProps,
  formatFunction,
  isBaseColumnPropertyVisible,
} from '../BaseColumn';
import { defaultValue } from './DateColumn';
import { analyzeDateColumn } from './DateColumnAnalyze';

const DateColumnManager: ComponentManagerFn<'DateColumn'> = (
  platform: Platform,
) => {
  let callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'DateColumn'>(platform, 'DateColumn'),
    type: 'DateColumn',
    name: 'Date Column',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'TriggerOnBlur',
          'TriggerOnCellClick',
          'TriggerOnChange',
          'TriggerOnFocus',
          'TriggerOnPaste',
          'TriggerAfterPaste',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        aliasType: ({ appUid, pageId, itemId }) => {
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'LOVGrid',
          );
          return parent ? 'LOV' : 'STORE';
        },
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime)
            return [
              'DateTime',
              'StringDateTime',
              'ServerDate',
              'ServerDateTime',
            ];
          return [
            'Date',
            'DateTime',
            'StringDateTime',
            'ServerDate',
            'ServerDateTime',
          ];
        },
        attributeRefreshCallback: (cb: () => void) => {
          callback = cb;
        },
      },
      format: {
        label: 'Format',
        type: 'string',
        category: 'Display',
      },
      prettyFormat: {
        label: 'Pretty Format',
        type: 'boolean',
        category: 'Display',
      },
      showTime: {
        label: 'Show Time',
        type: 'boolean',
      },
      hideTodayButton: {
        label: 'Hide Today Button',
        type: 'boolean',
      },
      disablePast: {
        label: 'Disable Past',
        type: 'boolean',
        helpText: 'Enable this to disable the past days',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      disableDateFunction: {
        label: 'Disable Date Function',
        type: 'controllerFunction',
        category: 'Controllers',
        helpText:
          'Provide a controller function to check which dates should be disabled. It should return a boolean value.',
      },
      minDateAttribute: {
        label: 'Min Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime', 'StringDateTime'];
          return ['Date', 'DateTime', 'StringDateTime'];
        },
      },
      maxDateAttribute: {
        label: 'Max Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime', 'StringDateTime'];
          return ['Date', 'DateTime', 'StringDateTime'];
        },
      },
      formatFunction,
    },
    onPropertyChange: (property, value) => {
      if (property === 'showTime') {
        callback();
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'showTime': {
          const store = platform.getParentStore(appUid, pageId, itemId);
          const attr = store?.getAttribute(itemData.viewAttribute);

          return (
            attr?.attributeType === 'DateTime' ||
            attr?.attributeType === 'StringDateTime'
          );
        }
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    analyze: analyzeDateColumn,
    category: 'Data Grid',
    icon: { iconName: 'calendar-edit' },
    replaceableWith: ['StringColumn'],
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The Date Column is a specialized input column used within a Data Grid to display and edit temporal values.
When a user clicks a cell in this column, it opens an interactive Calendar / Date Picker widget, allowing them to select a date (and optionally a time) without manual typing errors. It is essential for managing schedules, deadlines, or historical records directly within a table.

**1. Data Binding & Types**<br/>
To ensure correct sorting and storage, this column must bind to a compatible attribute type.
* **View Attribute:** Select the Data Store field. (Supported Types: Date, DateTime, StringDateTime).
* **Show Time:**
    * **Enabled:** The picker includes a clock/time selector.
    * **Prerequisite:** This option is only available if the underlying attribute is of type **DateTime** or **StringDateTime**. Standard Date fields cannot store time.

**2. Display Formatting**<br/>
You can control exactly how the date appears in the grid cell (when not being edited).
* **Format:** A format string defining the visual output.
    * **dd/MM/yyyy** → 25/12/2023
    * **MMM dd, yyyy** → Dec 25, 2023
    * **HH:mm** → 14:30 (for time-only views)
* **Pretty Format:** If enabled, the system applies a locale-aware, user-friendly format automatically.

**3. Constraints & Validation**<br/>
You can restrict which dates users are allowed to select to ensure data integrity.
* **Disable Past:** If enabled, users cannot select any date prior to "Today." Ideal for setting future deadlines.
* **Min / Max Date Attributes:**
    * **Purpose:** Bind these to other columns in the same row to create dynamic ranges.
    * **Use Case:** Bind Min Date to a StartDate column so the user cannot select an EndDate that occurs before the start.
* **Disable Date Function:** A controller function that returns true or false for specific dates. (Use Case: "Disable Weekends" or "Block National Holidays").

**4. Interaction & Triggers**<br/>
* **TriggerOnChange:** Fires logic immediately after a date is selected. (Use Case: When a "Due Date" is changed, automatically recalculate the "Days Remaining" column).
* **Server Validate On Change:** Triggers a backend validation call. If the server rejects the date (e.g., "Fiscal period is closed"), the cell reverts to its previous value.`,
    helpRenders: [
      {
        title: 'Date Column',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['label'],
        item: {
          children: [],
          itemId: 'dateColumn',
          parent: 'dataGrid',
          props: {
            label: { type: 'text', value: 'Date' },
            viewAttribute: 'someDate',
            width: 200,
          },
          type: 'DateColumn',
        },
      },
      {
        title: 'Editable Date Column',
        description: 'Turn on Updatable prop to enabled editing',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['updatable'],
        item: {
          children: [],
          itemId: 'dateColumn',
          parent: 'dataGrid',
          props: {
            insertable: {
              type: 'boolean',
              value: true,
            },
            label: { type: 'text', value: 'Date' },
            updatable: {
              type: 'boolean',
              value: true,
            },
            viewAttribute: 'someDate',
            width: 200,
          },
          type: 'DateColumn',
        },
      },
      {
        title: 'Conditional Editing',
        description:
          'Here is a sample expression to enable editing only the future dates',
        page: SamplePage,
        exampleId: 'e4',
        clearParentChildren: true,
        visibleProps: ['updatable'],
        item: {
          children: [],
          itemId: 'dateColumn',
          parent: 'dataGrid',
          props: {
            insertable: {
              type: 'boolean',
              value: true,
            },
            label: { type: 'text', value: 'Date' },
            updatable: {
              type: 'expression',
              value: 'new Date($.test.someDate) > new Date()',
            },
            viewAttribute: 'someDate',
            width: 200,
          },
          type: 'DateColumn',
        },
      },
      {
        title: 'With Time',
        description:
          'Show Time prop will be visible if the attribute is of type "DateTime"',
        page: SamplePage,
        exampleId: 'e5',
        clearParentChildren: true,
        visibleProps: ['showTime'],
        item: {
          children: [],
          itemId: 'dateColumn',
          parent: 'dataGrid',
          props: {
            insertable: {
              type: 'boolean',
              value: true,
            },
            label: { type: 'text', value: 'Date' },
            updatable: {
              type: 'boolean',
              value: true,
            },
            viewAttribute: 'someDateTime',
            width: 200,
            showTime: true,
          },
          type: 'DateColumn',
        },
      },
      {
        title: 'Date Format',
        description: 'Custom Date Format e.g. Credit Card Expiry',
        page: SamplePage,
        exampleId: 'e6',
        clearParentChildren: true,
        visibleProps: ['format'],
        item: {
          children: [],
          itemId: 'dateColumn',
          parent: 'dataGrid',
          props: {
            insertable: {
              type: 'boolean',
              value: true,
            },
            label: { type: 'text', value: 'Date' },
            updatable: {
              type: 'expression',
              value: 'true',
            },
            format: 'yyyy/MM',
            viewAttribute: 'someDate',
            width: 200,
          },
          type: 'DateColumn',
        },
      },
    ],
  };
};

export default DateColumnManager;
